import { format } from 'date-fns/format';
import { getYear } from 'date-fns/getYear';
import { parse } from 'date-fns/parse';
import { formatMonthYearFr, getPrimePendingMonthYear, getPrimePendingReferenceDate } from '@bloomays-lib/utils.shared';
import {
  BloomieTamSeniority,
  getNextTamSeniority,
  TAM_ANNUAL_MARGIN_OBJECTIVES,
  TAM_DEALS_SENIORITY_THRESHOLDS,
} from '@bloomays-lib/types.shared';
import {
  AnnualPrimeTableRow,
  AnnualTamPrimeAggregate,
  MonthlyPrimeNavigationTarget,
  MonthlyTamPrimeAggregate,
  MonthlyTamPrimeQueryResult,
  ValidatedTamPrimeAggregate,
} from '../types/primes';

import { formatDateToMonthYear, splitPrimeMonthLabel } from './monthlyPrime.utils';

export type AnnualCurrentMonthHeaderState = {
  monthLabel: string;
  monthName: string;
  year: string;
  status: 'validated' | 'pending';
  totalPrime: number;
  regularisation: number;
  bonus: number;
};

export type AnnualGoalHeaderState = {
  cumulativeMargin: number;
  targetAmount: number;
  percent: number;
  remainingAmount: number;
  progressBarPercent: number;
};

export type AnnualDealsGoalState = {
  signedDeals: number;
  targetSeniority: Exclude<BloomieTamSeniority, 'Director'> | null;
  targetThreshold: number | null;
  percent: number;
  progressBarPercent: number;
  isExceeded: boolean;
  isMaxSeniority: boolean;
  remainingDeals: number;
  exceededBy: number;
};

const monthYearSortKey = (monthYear: string): number => parse(monthYear, 'MM/yyyy', new Date()).getTime();

export const isCurrentYear = (year: string): boolean => year === format(new Date(), 'yyyy');

export const getAnnualCurrentMonthHeaderState = (
  aggregate: Pick<AnnualTamPrimeAggregate, 'validatedPrimes' | 'pendingPrimes'>,
): AnnualCurrentMonthHeaderState | null => {
  const currentMonthYear = getPrimePendingMonthYear();
  const currentReferenceDate = parse(currentMonthYear, 'MM/yyyy', new Date());

  if (aggregate.pendingPrimes !== null) {
    const monthLabel = formatMonthYearFr(currentReferenceDate);
    const { monthName, year } = splitPrimeMonthLabel(monthLabel);

    return {
      monthLabel,
      monthName,
      year,
      status: 'pending',
      totalPrime: aggregate.pendingPrimes.totalPrime,
      regularisation: aggregate.pendingPrimes.regularisation,
      bonus: aggregate.pendingPrimes.bonus,
    };
  }

  const validatedCurrentMonth = aggregate.validatedPrimes.find(
    (validatedPrime) => validatedPrime.monthYear === currentMonthYear,
  );

  if (!validatedCurrentMonth) {
    return null;
  }

  const monthLabel = formatMonthYearFr(currentReferenceDate);
  const { monthName, year } = splitPrimeMonthLabel(monthLabel);

  return {
    monthLabel,
    monthName,
    year,
    status: 'validated',
    totalPrime: validatedCurrentMonth.totalPrime,
    regularisation: validatedCurrentMonth.regularisation,
    bonus: validatedCurrentMonth.bonus,
  };
};

const toSummaryFields = (
  aggregate: Pick<
    MonthlyTamPrimeAggregate,
    | 'bonus'
    | 'regularisation'
    | 'regularisationReason'
    | 'totalFreelanceMarge'
    | 'totalBillingMarge'
    | 'totalMarge'
    | 'totalPrime'
    | 'totalDeals'
    | 'totalMissions'
  >,
): Pick<
  AnnualPrimeTableRow,
  | 'bonus'
  | 'regularisation'
  | 'regularisationReason'
  | 'totalFreelanceMarge'
  | 'totalBillingMarge'
  | 'totalMarge'
  | 'totalPrime'
  | 'totalDeals'
  | 'totalMissions'
> => ({
  bonus: aggregate.bonus,
  regularisation: aggregate.regularisation,
  regularisationReason: aggregate.regularisationReason ?? null,
  totalFreelanceMarge: aggregate.totalFreelanceMarge,
  totalBillingMarge: aggregate.totalBillingMarge,
  totalMarge: aggregate.totalMarge,
  totalPrime: aggregate.totalPrime,
  totalDeals: aggregate.totalDeals,
  totalMissions: aggregate.totalMissions,
});

export const mapAnnualTamPrimeToTableRows = (aggregate: AnnualTamPrimeAggregate): AnnualPrimeTableRow[] => {
  const rows: AnnualPrimeTableRow[] = aggregate.validatedPrimes.map((validatedPrime) => ({
    rowId: `validated-${validatedPrime.id}`,
    monthYear: validatedPrime.monthYear,
    status: 'validated',
    ...toSummaryFields(validatedPrime),
  }));

  if (aggregate.pendingPrimes !== null) {
    const pendingMonthYear = getPrimePendingMonthYear();
    rows.push({
      rowId: `pending-${pendingMonthYear}`,
      monthYear: pendingMonthYear,
      status: 'pending',
      ...toSummaryFields(aggregate.pendingPrimes),
    });
  }

  return rows.sort((left, right) => monthYearSortKey(left.monthYear) - monthYearSortKey(right.monthYear));
};

export const getAnnualDealsGoalState = (
  aggregate: AnnualTamPrimeAggregate,
  selectedYear: string,
): AnnualDealsGoalState => {
  const validatedDeals = aggregate.validatedPrimes.reduce(
    (total, validatedPrime) => total + validatedPrime.totalDeals,
    0,
  );
  const pendingDeals = isCurrentYear(selectedYear) ? aggregate.pendingPrimes?.totalDeals ?? 0 : 0;
  const signedDeals = validatedDeals + pendingDeals;
  const targetSeniority = getNextTamSeniority(aggregate.tamSeniority);

  if (targetSeniority === null) {
    return {
      signedDeals,
      targetSeniority: null,
      targetThreshold: null,
      percent: 0,
      progressBarPercent: 0,
      isExceeded: false,
      isMaxSeniority: true,
      remainingDeals: 0,
      exceededBy: 0,
    };
  }

  const targetThreshold = TAM_DEALS_SENIORITY_THRESHOLDS[targetSeniority];
  const percent = targetThreshold === 0 ? 0 : Math.round((signedDeals / targetThreshold) * 100);
  const isExceeded = signedDeals >= targetThreshold;

  return {
    signedDeals,
    targetSeniority,
    targetThreshold,
    percent,
    progressBarPercent: Math.min(percent, 100),
    isExceeded,
    isMaxSeniority: false,
    remainingDeals: Math.max(0, targetThreshold - signedDeals),
    exceededBy: Math.max(0, signedDeals - targetThreshold),
  };
};

export const getAnnualGoalHeaderState = (aggregate: AnnualTamPrimeAggregate): AnnualGoalHeaderState => {
  const validatedTotalMargin = aggregate.validatedPrimes.reduce(
    (total, validatedPrime) => total + validatedPrime.totalMarge,
    0,
  );
  const pendingTotalMargin = aggregate.pendingPrimes?.totalMarge ?? 0;
  const cumulativeMargin = validatedTotalMargin + pendingTotalMargin;
  const targetAmount = TAM_ANNUAL_MARGIN_OBJECTIVES[aggregate.tamSeniority];
  const percent = aggregate.goalCompletion;
  return {
    cumulativeMargin,
    targetAmount,
    percent,
    remainingAmount: Math.max(0, targetAmount - cumulativeMargin),
    progressBarPercent: Math.min(percent, 100),
  };
};

export const yearToReferenceDate = (year: string): Date => new Date(getYear(parse(year, 'yyyy', new Date())), 0, 1);

export const referenceDateToYear = (date: Date): string => format(date, 'yyyy');

export const buildCLevelAnnualDetailPath = (tamEmail: string, year: string): string => {
  const search = new URLSearchParams({ tam: tamEmail, year }).toString();
  return `/primes/dashboard/details?${search}`;
};

export const buildCLevelTamMonthlyDetailPath = (
  tamEmail: string,
  referenceDate: Date = getPrimePendingReferenceDate(),
): string => {
  const search = new URLSearchParams({ tam: tamEmail.trim() }).toString();
  return `/primes/${formatDateToMonthYear(referenceDate)}?${search}`;
};

export const formatMonthYearToUrlParam = (monthYear: string): string =>
  formatDateToMonthYear(parse(monthYear, 'MM/yyyy', new Date()));

const toValidatedTamPrimeAggregate = (
  validatedPrime: AnnualTamPrimeAggregate['validatedPrimes'][number],
): ValidatedTamPrimeAggregate => ({
  __typename: 'ValidatedTamPrime',
  id: validatedPrime.id,
  monthYear: validatedPrime.monthYear,
  validationDate: validatedPrime.validationDate,
  primes: validatedPrime.primes,
  bonus: validatedPrime.bonus,
  regularisation: validatedPrime.regularisation,
  regularisationReason: validatedPrime.regularisationReason ?? null,
  totalFreelanceMarge: validatedPrime.totalFreelanceMarge,
  totalBillingMarge: validatedPrime.totalBillingMarge,
  totalMarge: validatedPrime.totalMarge,
  totalPrime: validatedPrime.totalPrime,
  totalDeals: validatedPrime.totalDeals,
  totalMissions: validatedPrime.totalMissions,
});

export const mapAnnualRowToMonthlyTamPrimeQueryResult = (
  aggregate: AnnualTamPrimeAggregate,
  row: AnnualPrimeTableRow,
): MonthlyTamPrimeQueryResult => {
  if (row.status === 'pending') {
    if (aggregate.pendingPrimes === null) {
      throw new Error(`mapAnnualRowToMonthlyTamPrimeQueryResult: no pending prime for row ${row.rowId}`);
    }
    return aggregate.pendingPrimes;
  }

  const validatedPrime = aggregate.validatedPrimes.find((candidate) => candidate.monthYear === row.monthYear);
  if (!validatedPrime) {
    throw new Error(`mapAnnualRowToMonthlyTamPrimeQueryResult: no validated prime for row ${row.rowId}`);
  }

  return toValidatedTamPrimeAggregate(validatedPrime);
};

export type BuildMonthlyPrimeNavigationTargetParams = {
  aggregate: AnnualTamPrimeAggregate;
  row: AnnualPrimeTableRow;
  tamEmail: string;
  isTamPreview: boolean;
};

export const buildMonthlyPrimeNavigationTarget = ({
  aggregate,
  row,
  tamEmail,
  isTamPreview,
}: BuildMonthlyPrimeNavigationTargetParams): MonthlyPrimeNavigationTarget => {
  const mmYyyy = formatMonthYearToUrlParam(row.monthYear);
  const trimmedTamEmail = tamEmail.trim();
  const search = isTamPreview ? `?${new URLSearchParams({ tam: trimmedTamEmail }).toString()}` : '';

  return {
    pathname: `/primes/${mmYyyy}`,
    search,
    state: {
      preloadedMonthlyTamPrime: mapAnnualRowToMonthlyTamPrimeQueryResult(aggregate, row),
    },
  };
};
